import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { clientsData } from '../data/companyData';
import { Building2, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';

const ClientsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="clients-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#001582] to-[#001ea7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#FFC107] rounded-full" />
              <span className="text-white text-sm font-semibold uppercase tracking-wider">Klien</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Dipercaya oleh <span className="text-[#FFC107]">Perusahaan</span> Terkemuka
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Kami bangga telah bekerja sama dengan berbagai perusahaan terkemuka 
              di Indonesia dalam menyediakan solusi engineering dan manufacturing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-white rounded-xl px-6 py-3 flex items-center gap-3 shadow-sm">
              <span className="font-heading font-bold text-2xl text-[#001582]">{clientsData.length}</span>
              <span className="text-slate-600 text-sm">Total Klien</span>
            </div>
            <div className="bg-white rounded-xl px-6 py-3 flex items-center gap-3 shadow-sm">
              <span className="font-heading font-bold text-2xl text-[#001582]">5+</span>
              <span className="text-slate-600 text-sm">Sektor Industri</span>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Grid */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              Daftar <span className="text-[#001582]">Klien</span>
            </h2>
            <p className="text-slate-600 text-lg">
              Perusahaan-perusahaan yang telah mempercayakan kebutuhan engineering mereka kepada kami.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {clientsData.map((client, index) => (
              <motion.div
                key={client.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group"
              >
                <div className="bg-white rounded-2xl border border-slate-100 p-6 h-full hover:border-[#001582]/20 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#001582]/10 transition-colors overflow-hidden">
                      {client.logo ? (
                        <img 
                          src={client.logo} 
                          alt={client.name}
                          className="w-12 h-12 object-contain"
                        />
                      ) : (
                        <Building2 className="w-6 h-6 text-slate-400 group-hover:text-[#001582] transition-colors" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-[#1a1a1a] text-sm leading-tight group-hover:text-[#001582] transition-colors">
                        {client.name}
                      </h4>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#001582] to-[#000d5c]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              Bergabunglah dengan Klien Kami
            </h3>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
              Jadilah bagian dari perusahaan-perusahaan terkemuka yang telah mempercayakan 
              kebutuhan engineering dan manufacturing mereka kepada kami.
            </p>
            <a 
              href="https://wa.me/6285210005588" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button 
                size="lg"
                className="bg-[#FFC107] text-[#001582] hover:bg-[#FFD54F] font-bold px-8 py-6 rounded-xl"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Mulai Kerjasama
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClientsPage;
